import QRCode from 'qrcode'
import type { Order } from './db'
import type { ShopSummary } from './api'

/**
 * Nói chuyện với sigoPOS Print Agent — chương trình nhỏ chạy nền ngay trên máy quầy.
 *
 * Nhờ nó mà app chạy trong trình duyệt thường vẫn in được bill, không phải đóng gói
 * thành app desktop chỉ để với tới máy in.
 */

const AGENT = 'http://127.0.0.1:9110'

/**
 * File cài đặt agent, do scripts/pack_agent.py đẩy lên.
 *
 * Đặt trên trang giới thiệu chứ không phải app: đường dẫn này còn dùng để đọc qua điện
 * thoại cho người ta gõ vào máy tính, mà app thì phải đăng nhập mới vào được.
 */
export const AGENT_DOWNLOAD = 'https://sigopos.com/download/sigoPOS-PrintAgent.exe'

export type PrinterKind = 'Windows' | 'Network' | 'Serial'

export interface PrinterConfig {
  kind: PrinterKind
  address: string
  port: number
  baudRate: number
  paperWidthMm: 58 | 80
  openDrawer: boolean
  cut: boolean
}

export const defaultPrinter: PrinterConfig = {
  kind: 'Network',
  address: '',
  port: 9100,
  baudRate: 9600,
  paperWidthMm: 58,
  openDrawer: false,
  cut: true,
}

export interface AgentInfo {
  name: string
  version: string
  machine: string
}

async function agentFetch(path: string, init?: RequestInit, timeoutMs = 12_000) {
  // Agent không chạy thì fetch treo cho tới khi hệ điều hành bỏ cuộc — người bán
  // đứng nhìn nút quay. Cắt sớm để còn kịp báo "chưa cài agent".
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), timeoutMs)
  try {
    return await fetch(AGENT + path, { ...init, signal: abort.signal })
  } finally {
    clearTimeout(timer)
  }
}

/** Có agent trên máy này không. Dùng để ẩn nút In bill khi chưa cài. */
export async function probeAgent(): Promise<AgentInfo | null> {
  try {
    const res = await agentFetch('/agent/ping', undefined, 2500)
    return res.ok ? ((await res.json()) as AgentInfo) : null
  } catch {
    return null
  }
}

export async function listPrinters(): Promise<{ windows: string[]; serial: string[] }> {
  const res = await agentFetch('/agent/printers', undefined, 5000)
  if (!res.ok) throw new Error('Không đọc được danh sách máy in')
  return (await res.json()) as { windows: string[]; serial: string[] }
}

/**
 * Mã QR tra cứu hoá đơn điện tử, dựng thành ảnh ngay trên máy quầy.
 *
 * Dựng ở đây chứ không ở chương trình in: máy quầy có sẵn thư viện QR, còn agent thì
 * đóng gói thành một file exe duy nhất, thêm thư viện vào đó là file cài nặng thêm cho
 * mọi người kể cả quán không dùng hoá đơn điện tử. Agent chỉ việc vẽ ảnh nhận được.
 *
 * Không dựng được thì thôi — tờ bill vẫn in ra với số hoá đơn và mã cơ quan thuế bằng chữ,
 * mất mã QR không đáng để mất cả tờ bill.
 */
async function lookupQrImage(order: Order): Promise<string | null> {
  const url = order.einvoice?.lookupUrl
  if (!url) return null

  try {
    return await QRCode.toDataURL(url, {
      width: 220,
      margin: 1,
      // Đen trên trắng cố định. Máy in nhiệt chỉ có hai màu, và ảnh xám thì in ra lốm đốm.
      color: { dark: '#000000', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    })
  } catch {
    return null
  }
}

function buildPayload(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
  opts: { provisional?: boolean; cashierName?: string; qrImage?: string | null } = {},
) {
  // Bản tạm tính chưa phải hoá đơn — không in số hoá đơn lên đó, kẻo khách cầm tờ giấy
  // chưa trả tiền mà tưởng đã có hoá đơn hợp lệ.
  const einvoice = opts.provisional ? null : (order.einvoice ?? null)

  return {
    target: {
      kind: printer.kind,
      address: printer.address,
      port: printer.port,
      baudRate: printer.baudRate,
      paperWidthMm: printer.paperWidthMm,
      // Bản tạm tính không cắt giấy và không mở két — khách còn ngồi, chưa trả tiền.
      openDrawer: opts.provisional ? false : printer.openDrawer,
      cut: printer.cut,
    },
    receipt: {
      shopName: shop?.name ?? 'Quán của tôi',
      address: shop?.address ?? null,
      phone: shop?.phone ?? null,
      taxCode: shop?.taxCode ?? null,
      code: order.code,
      printedAt: new Date().toISOString(),
      tableName: order.tableName,
      cashierName: opts.cashierName ?? null,
      lines: order.items.map((i) => ({
        name: i.name,
        option: i.optionLabel,
        qtyMilli: i.qtyMilli,
        unitPrice: i.unitPrice,
        lineTotal: i.lineTotal,
        note: i.note,
      })),
      subtotal: order.subtotal,
      discount: order.discount,
      total: order.total,
      paymentMethod: opts.provisional ? null : order.paymentMethod,
      amountGiven: order.amountGiven,
      changeGiven: order.changeGiven,
      footer: null,
      qrPayload: null,
      isProvisional: opts.provisional ?? false,

      // Hoá đơn điện tử. Chỉ in khi đã có mã cơ quan thuế — tờ đang nằm trong hàng đợi thì
      // chưa là hoá đơn, in số lên là nói với khách một điều chưa đúng.
      invoiceSeries: einvoice?.status === 'Issued' ? einvoice.series : null,
      invoiceNo: einvoice?.status === 'Issued' ? einvoice.invoiceNo : null,
      taxAuthorityCode: einvoice?.status === 'Issued' ? einvoice.taxAuthorityCode : null,
      qrImageBase64: einvoice?.status === 'Issued' ? (opts.qrImage ?? null) : null,
    },
  }
}

export async function printReceipt(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
  opts: { provisional?: boolean; cashierName?: string } = {},
): Promise<void> {
  const qrImage = opts.provisional ? null : await lookupQrImage(order)

  let res: Response
  try {
    res = await agentFetch('/agent/print', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(order, shop, printer, { ...opts, qrImage })),
    })
  } catch {
    throw new Error('Chưa chạy chương trình in trên máy này. Mở sigoPOS Print Agent rồi thử lại.')
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null
    throw new Error(body?.message ?? 'Không in được.')
  }
}

/** Ảnh xem trước đúng y tờ giấy sẽ ra — để thử máy in mà không tốn giấy. */
export async function previewReceipt(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
): Promise<string> {
  const res = await agentFetch('/agent/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildPayload(order, shop, printer, { qrImage: await lookupQrImage(order) })),
  })
  if (!res.ok) throw new Error('Không dựng được bản xem trước')
  return URL.createObjectURL(await res.blob())
}

export async function openDrawer(printer: PrinterConfig): Promise<void> {
  const res = await agentFetch('/agent/drawer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: printer.kind,
      address: printer.address,
      port: printer.port,
      baudRate: printer.baudRate,
      paperWidthMm: printer.paperWidthMm,
    }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null
    throw new Error(body?.message ?? 'Không mở được két.')
  }
}
